import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { LogOut, Plus, Pencil, Trash2, X, Store } from "lucide-react";

const ICON_OPTIONS = ["WashingMachine", "Wind", "Layers", "Refrigerator", "Flame", "AirVent", "ChefHat", "Snowflake", "Warehouse", "Package"];

const COLLECTIONS = {
  categories: {
    label: "Categorías",
    fields: [
      { k: "name", label: "Nombre" },
      { k: "key", label: "Clave (slug, sin espacios)" },
      { k: "blurb", label: "Descripción corta" },
      { k: "icon", label: "Ícono", type: "select", options: ICON_OPTIONS },
      { k: "image", label: "URL de imagen (opcional)" },
      { k: "featured", label: "Destacada (tarjeta grande)", type: "checkbox" },
      { k: "order", label: "Orden", type: "number" },
    ],
  },
  products: {
    label: "Productos",
    fields: [
      { k: "name", label: "Nombre" },
      { k: "category", label: "Categoría" },
      { k: "description", label: "Descripción", type: "textarea" },
      { k: "image", label: "URL de imagen" },
      { k: "order", label: "Orden", type: "number" },
    ],
  },
  social_posts: {
    label: "Nuevos Ingresos",
    fields: [
      { k: "platform", label: "Plataforma", type: "select", options: ["TikTok", "Instagram", "Facebook"] },
      { k: "url", label: "Enlace al video/publicación" },
      { k: "thumbnail", label: "URL de miniatura (imagen)" },
      { k: "caption", label: "Descripción", type: "textarea" },
      { k: "order", label: "Orden", type: "number" },
    ],
  },
  reviews: {
    label: "Reseñas",
    hint: "Publica únicamente reseñas reales copiadas de Google.",
    fields: [
      { k: "author", label: "Nombre del cliente" },
      { k: "rating", label: "Estrellas (1-5)", type: "number" },
      { k: "text", label: "Reseña", type: "textarea" },
      { k: "location", label: "Sucursal" },
      { k: "date", label: "Fecha (texto)" },
    ],
  },
  locations: {
    label: "Ubicaciones",
    fields: [
      { k: "name", label: "Nombre de sucursal" },
      { k: "address", label: "Dirección" },
      { k: "address_note", label: "Nota (opcional)" },
      { k: "phone_display", label: "Teléfono visible (ej. +504 8806-9676)" },
      { k: "phone", label: "Teléfono para llamadas (con +504, sin espacios)" },
      { k: "maps_url", label: "Enlace de Google Maps" },
      { k: "photo", label: "URL de foto de la fachada (opcional)" },
      { k: "lat", label: "Latitud (opcional)" },
      { k: "lng", label: "Longitud (opcional)" },
      { k: "order", label: "Orden", type: "number" },
    ],
  },
};

const itemLabel = (item) => item.name || item.author || item.platform || item.key || item.id;

function Login({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("er_token", data.token);
      onSuccess(data.user);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-secondary/50 px-5" data-testid="admin-login">
      <form onSubmit={submit} className="w-full max-w-sm border border-border bg-white p-8">
        <span className="flex h-10 w-10 items-center justify-center bg-primary text-sm font-extrabold text-white">ER</span>
        <h1 className="mt-5 text-2xl font-extrabold tracking-tight text-primary">Panel administrativo</h1>
        <p className="mt-1 text-sm text-muted-foreground">Electrodomésticos Rivera</p>
        <label className="mt-6 block text-xs font-semibold uppercase tracking-widest text-muted-foreground">Email</label>
        <Input data-testid="admin-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-2 h-11" />
        <label className="mt-4 block text-xs font-semibold uppercase tracking-widest text-muted-foreground">Contraseña</label>
        <Input data-testid="admin-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="mt-2 h-11" />
        {error && (
          <p data-testid="admin-login-error" className="mt-3 border border-destructive/30 bg-destructive/10 p-3 text-xs font-semibold text-destructive">
            {error}
          </p>
        )}
        <Button data-testid="admin-login-button" type="submit" disabled={loading} className="mt-6 h-12 w-full rounded-none font-semibold">
          {loading ? "Ingresando..." : "Ingresar"}
        </Button>
      </form>
    </div>
  );
}

function Field({ field, value, onChange, testidPrefix }) {
  const id = `${testidPrefix}-field-${field.k}`;
  if (field.type === "textarea")
    return <Textarea data-testid={id} value={value} onChange={(e) => onChange(field.k, e.target.value)} rows={3} className="mt-1" />;
  if (field.type === "select")
    return (
      <select
        data-testid={id}
        value={value}
        onChange={(e) => onChange(field.k, e.target.value)}
        className="mt-1 h-11 w-full border border-input bg-white px-3 text-sm"
      >
        <option value="">— Seleccionar —</option>
        {field.options.map((o) => (
          <option key={o} value={o}>{o}</option>
        ))}
      </select>
    );
  if (field.type === "checkbox")
    return (
      <input
        data-testid={id}
        type="checkbox"
        checked={!!value}
        onChange={(e) => onChange(field.k, e.target.checked)}
        className="mt-2 h-5 w-5 accent-[#0A2540]"
      />
    );
  return (
    <Input
      data-testid={id}
      type={field.type === "number" ? "number" : "text"}
      value={value}
      onChange={(e) => onChange(field.k, e.target.value)}
      className="mt-1 h-11"
    />
  );
}

function Manager({ collKey, config }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api
      .get(`/admin/items/${collKey}`)
      .then((r) => setItems(r.data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, [collKey]);

  useEffect(() => {
    load();
  }, [load]);

  const startNew = () => {
    const f = {};
    config.fields.forEach((fl) => {
      f[fl.k] = fl.type === "checkbox" ? false : fl.type === "number" ? 0 : "";
    });
    setForm(f);
  };

  const onChange = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    const payload = { ...form };
    config.fields.forEach((fl) => {
      if (fl.type === "number") payload[fl.k] = Number(payload[fl.k]) || 0;
    });
    try {
      if (form.id) await api.put(`/admin/items/${collKey}/${form.id}`, payload);
      else await api.post(`/admin/items/${collKey}`, payload);
      toast.success("Guardado correctamente");
      setForm(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("¿Eliminar este elemento?")) return;
    try {
      await api.delete(`/admin/items/${collKey}/${id}`);
      toast.success("Eliminado");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div data-testid={`admin-manager-${collKey}`}>
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-extrabold tracking-tight text-primary">{config.label}</h2>
          {config.hint && <p className="mt-1 text-xs text-muted-foreground">{config.hint}</p>}
        </div>
        <Button data-testid={`admin-new-${collKey}`} onClick={startNew} className="rounded-none font-semibold">
          <Plus className="mr-2 h-4 w-4" /> Nuevo
        </Button>
      </div>

      {form && (
        <div className="mt-6 border border-border bg-white p-6" data-testid={`admin-form-${collKey}`}>
          <div className="flex items-center justify-between">
            <p className="text-sm font-extrabold text-primary">{form.id ? "Editar" : "Nuevo"} — {config.label}</p>
            <button data-testid={`admin-form-close-${collKey}`} onClick={() => setForm(null)} className="text-muted-foreground hover:text-primary">
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
            {config.fields.map((fl) => (
              <div key={fl.k} className={fl.type === "textarea" ? "sm:col-span-2" : ""}>
                <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{fl.label}</label>
                <Field field={fl} value={form[fl.k]} onChange={onChange} testidPrefix={`admin-${collKey}`} />
              </div>
            ))}
          </div>
          <Button data-testid={`admin-save-${collKey}`} onClick={save} disabled={saving} className="mt-6 h-11 rounded-none font-semibold">
            {saving ? "Guardando..." : "Guardar"}
          </Button>
        </div>
      )}

      <div className="mt-6 divide-y divide-border border border-border bg-white" data-testid={`admin-list-${collKey}`}>
        {items.length === 0 && <p className="p-6 text-sm text-muted-foreground">Sin elementos todavía.</p>}
        {items.map((item, i) => (
          <div key={item.id} className="flex items-center justify-between gap-4 p-4" data-testid={`admin-item-${collKey}-${i}`}>
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-primary">{itemLabel(item)}</p>
              <p className="truncate text-xs text-muted-foreground">{item.blurb || item.text || item.address || item.caption || item.category || ""}</p>
            </div>
            <div className="flex shrink-0 gap-2">
              <button
                data-testid={`admin-edit-${collKey}-${i}`}
                onClick={() => setForm(item)}
                className="flex h-9 w-9 items-center justify-center border border-border text-primary hover:bg-secondary"
              >
                <Pencil className="h-4 w-4" />
              </button>
              <button
                data-testid={`admin-delete-${collKey}-${i}`}
                onClick={() => del(item.id)}
                className="flex h-9 w-9 items-center justify-center border border-border text-destructive hover:bg-destructive/10"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SettingsForm() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/content").then((r) => setForm(r.data.settings || {})).catch(() => setForm({}));
  }, []);

  if (!form) return <p className="text-sm text-muted-foreground">Cargando...</p>;

  const fields = [
    { k: "whatsapp", label: "WhatsApp (código país + número, sin + ni espacios)" },
    { k: "phone", label: "Teléfono para llamadas (con +504)" },
    { k: "phone_display", label: "Teléfono visible (ej. +504 8746-5034)" },
    { k: "tiktok", label: "URL TikTok" },
    { k: "instagram", label: "URL Instagram" },
    { k: "facebook", label: "URL Facebook" },
  ];

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", form);
      toast.success("Ajustes guardados");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-settings-form" className="border border-border bg-white p-6">
      <h2 className="text-xl font-extrabold tracking-tight text-primary">Ajustes del sitio</h2>
      <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
        {fields.map((fl) => (
          <div key={fl.k}>
            <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{fl.label}</label>
            <Input
              data-testid={`admin-settings-field-${fl.k}`}
              value={form[fl.k] || ""}
              onChange={(e) => setForm((f) => ({ ...f, [fl.k]: e.target.value }))}
              className="mt-1 h-11"
            />
          </div>
        ))}
      </div>
      <Button data-testid="admin-settings-save" onClick={save} disabled={saving} className="mt-6 h-11 rounded-none font-semibold">
        {saving ? "Guardando..." : "Guardar ajustes"}
      </Button>
    </div>
  );
}

export default function Admin() {
  const [user, setUser] = useState(undefined);
  const [tab, setTab] = useState("categories");

  useEffect(() => {
    const token = localStorage.getItem("er_token");
    if (!token) return setUser(null);
    api
      .get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => {
        localStorage.removeItem("er_token");
        setUser(null);
      });
  }, []);

  if (user === undefined)
    return (
      <div className="flex min-h-screen items-center justify-center bg-secondary/50">
        <p className="text-sm font-semibold text-muted-foreground">Cargando...</p>
      </div>
    );

  if (!user) return <Login onSuccess={setUser} />;

  const tabs = [...Object.entries(COLLECTIONS).map(([k, v]) => ({ k, label: v.label })), { k: "settings", label: "Ajustes" }];

  return (
    <div className="min-h-screen bg-secondary/50" data-testid="admin-dashboard">
      <header className="border-b border-border bg-white">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-5">
          <div className="flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center bg-primary text-sm font-extrabold text-white">
              <Store className="h-4 w-4" />
            </span>
            <div className="leading-tight">
              <p className="text-sm font-extrabold text-primary">Electrodomésticos Rivera</p>
              <p className="text-[11px] text-muted-foreground">Panel administrativo · {user.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a data-testid="admin-view-site" href="/" className="hidden text-sm font-semibold text-primary/70 hover:text-primary sm:block">
              Ver sitio
            </a>
            <button
              data-testid="admin-logout-button"
              onClick={() => {
                localStorage.removeItem("er_token");
                api.post("/auth/logout").catch(() => {});
                setUser(null);
              }}
              className="flex h-10 w-10 items-center justify-center border border-border text-primary hover:bg-secondary"
              aria-label="Cerrar sesión"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </header>
      <div className="mx-auto max-w-6xl px-5 py-8">
        <div className="flex flex-wrap gap-2" data-testid="admin-tabs">
          {tabs.map((t) => (
            <button
              key={t.k}
              data-testid={`admin-tab-${t.k}`}
              onClick={() => setTab(t.k)}
              className={`h-10 border px-4 text-sm font-semibold transition-colors duration-200 ${
                tab === t.k ? "border-primary bg-primary text-white" : "border-border bg-white text-primary hover:bg-secondary"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="mt-8">
          {tab === "settings" ? <SettingsForm /> : <Manager collKey={tab} config={COLLECTIONS[tab]} />}
        </div>
      </div>
    </div>
  );
}
